import type { RecommendedAccosResponseMapper } from '@/scopes/products/infrastructure/api/recommended-accos/recommendedAccosResponseMapper'
import { AccoListerAcco } from '@/scopes/common/domain/acco-lister/types'

import {
  createQueryStringSerializer,
  type QueryStringSerializer,
} from '@/scopes/common/infrastructure/query-strings/queryStringSerializer'
import { createRecommendedAccosResponseMapper } from '@/scopes/products/infrastructure/api/recommended-accos/recommendedAccosResponseMapper'
import defaultFetch from 'isomorphic-unfetch'

interface Dependencies {
  env: {
    NEXT_PUBLIC_API_BASE_URL: string
  }
  fetch: typeof defaultFetch
  queryStringSerializer: QueryStringSerializer
  recommendedAccosResponseMapper: RecommendedAccosResponseMapper
}

export interface GetRecommendedAccosParametersSession {
  accoId: number
  timeSpent: number
}

export interface GetRecommendedAccosParameters {
  contextItemId: string
  currentAccoId: number | null
  sessions: GetRecommendedAccosParametersSession[]
}

export function createRecommendedAccosAPIClient(dependencies: Partial<Dependencies> = {}) {
  const {
    env = {
      NEXT_PUBLIC_API_BASE_URL: process.env.NEXT_PUBLIC_API_BASE_URL,
    },
    fetch = defaultFetch,
    queryStringSerializer = createQueryStringSerializer(),
    recommendedAccosResponseMapper = createRecommendedAccosResponseMapper(),
  } = dependencies as Dependencies

  async function getRecommendedAccos({
    contextItemId,
    currentAccoId,
    sessions,
  }: GetRecommendedAccosParameters): Promise<AccoListerAcco[]> {
    const fetchOptions: RequestInit = {
      method: 'POST',
      referrerPolicy: 'no-referrer-when-downgrade',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        accoId: String(currentAccoId),
        weights: sessions.map(({ accoId, timeSpent }) => ({ accoId: String(accoId), timeSpent })),
      }),
    }

    const queryString = queryStringSerializer.serialize({
      contextItemId,
    })

    const response = await fetch(
      `${env.NEXT_PUBLIC_API_BASE_URL}/RecommendedAccoListerApi/GetRecommendedAccos?${queryString}`,
      fetchOptions,
    )

    const data = await response.json()

    return recommendedAccosResponseMapper.map(data)
  }

  return { getRecommendedAccos }
}
